import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardContent } from './ui/card';
import {
  CheckCircle2,
  Loader2,
  Search,
  Clock,
  Mail,
  Trash2,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../hooks/useFetch';

/**
 * 3-phase deletion status tracker.
 *
 * Exposes a public lookup form (paste DEL-XXXXXXXX request ID) plus the
 * visual timeline reused by the post-submit success view.
 *
 * Apple, Google, and most privacy regulators expect a transparent SLA
 * countdown — this component makes that visible to both end-users and
 * OAuth verifiers without exposing any PII beyond the request ID.
 */

const PHASES = [
  {
    key: 'received',
    label: 'Request received',
    labelEs: 'Solicitud recibida',
    description: "We've logged your request and notified our admin team.",
    descriptionEs: 'Registramos tu solicitud y notificamos a nuestro equipo.',
    icon: Mail,
  },
  {
    key: 'in_progress',
    label: 'In progress',
    labelEs: 'En progreso',
    description:
      'Our team is removing your account and associated data from our systems.',
    descriptionEs:
      'Nuestro equipo está eliminando tu cuenta y los datos asociados.',
    icon: Trash2,
  },
  {
    key: 'completed',
    label: 'Deleted',
    labelEs: 'Eliminado',
    description:
      'Your data has been permanently deleted (within the 30-day SLA).',
    descriptionEs:
      'Tus datos se eliminaron permanentemente (dentro del SLA de 30 días).',
    icon: ShieldCheck,
  },
];

function phaseIndex(phase) {
  const idx = PHASES.findIndex((p) => p.key === phase);
  return idx === -1 ? 0 : idx;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return iso;
  }
}

export function DeletionStatusTimeline({ record, isSpanish = false }) {
  if (!record || !record.timeline) return null;
  const { phase, sla_days, sla_deadline, days_elapsed, days_remaining } =
    record.timeline;
  const activeIdx = phaseIndex(phase);

  return (
    <div className="space-y-4" data-testid="deletion-status-timeline">
      {/* SLA pill */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>
            {isSpanish
              ? `Compromiso de eliminación en ${sla_days} días`
              : `${sla_days}-day deletion commitment`}
          </span>
        </div>
        {phase !== 'completed' && days_remaining !== null && (
          <span
            className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium"
            data-testid="deletion-sla-remaining"
          >
            {isSpanish
              ? `${days_remaining} días restantes`
              : `${days_remaining} days remaining`}
          </span>
        )}
        {phase === 'completed' && (
          <span
            className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-medium"
            data-testid="deletion-sla-completed"
          >
            {isSpanish ? 'Completado' : 'Completed'}
          </span>
        )}
      </div>

      {/* 3-step timeline */}
      <ol className="relative border-l-2 border-muted-foreground/20 ml-3 space-y-5">
        {PHASES.map((p, idx) => {
          const Icon = p.icon;
          const reached = idx <= activeIdx;
          const isCurrent = idx === activeIdx && phase !== 'completed';
          const tsField =
            p.key === 'received'
              ? record.submitted_at
              : p.key === 'in_progress'
              ? record.processed_at
              : record.completed_at;
          return (
            <li
              key={p.key}
              className="ml-5 pb-1"
              data-testid={`deletion-phase-${p.key}`}
              data-active={reached ? 'true' : 'false'}
            >
              <span
                className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  reached
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'bg-background border-muted-foreground/30 text-muted-foreground'
                } ${isCurrent ? 'ring-2 ring-primary/30 ring-offset-1' : ''}`}
              >
                {reached && p.key === 'completed' ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Icon className="h-3 w-3" aria-hidden="true" />
                )}
              </span>
              <div>
                <p
                  className={`text-sm font-semibold ${
                    reached ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {isSpanish ? p.labelEs : p.label}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSpanish ? p.descriptionEs : p.description}
                </p>
                {tsField && (
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    {formatDate(tsField)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {sla_deadline && phase !== 'completed' && (
        <p className="text-xs text-muted-foreground">
          {isSpanish
            ? `Fecha límite del SLA: ${formatDate(sla_deadline)}`
            : `SLA deadline: ${formatDate(sla_deadline)}`}
          {days_elapsed !== null && (
            <>
              {' '}
              ·{' '}
              {isSpanish
                ? `${days_elapsed} día(s) transcurrido(s)`
                : `${days_elapsed} day(s) elapsed`}
            </>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * Public lookup widget — user pastes their DEL-XXXXXXXX request ID and
 * we render the visual timeline above. Failure states are surfaced
 * inline (404 / 500).
 */
export default function DeletionStatusTracker({ isSpanish = false, defaultRequestId = '' }) {
  const [requestId, setRequestId] = useState(defaultRequestId);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async (id) => {
    const clean = (id || '').trim().toUpperCase();
    if (!clean) {
      setError(
        isSpanish
          ? 'Ingresa tu ID de solicitud (formato DEL-XXXXXXXX)'
          : 'Enter your request ID (format DEL-XXXXXXXX)'
      );
      return;
    }
    setLoading(true);
    setError(null);
    setRecord(null);
    try {
      const { data, error: apiError, status } = await apiFetch(
        `/api/privacy/deletion-status/${encodeURIComponent(clean)}`
      );
      if (apiError || !data) {
        const msg =
          status === 404
            ? isSpanish
              ? 'No encontramos esa solicitud. Verifica el ID.'
              : "We couldn't find that request. Double-check the ID."
            : isSpanish
            ? 'No se pudo obtener el estado. Intenta de nuevo.'
            : 'Could not fetch status. Please try again.';
        setError(msg);
        return;
      }
      setRecord(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DeletionStatusTracker] fetch failed', e);
      setError(
        isSpanish
          ? 'Error al consultar el estado. Intenta de nuevo.'
          : 'Error checking status. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [isSpanish]);

  // Auto-load if a defaultRequestId is provided.
  React.useEffect(() => {
    if (defaultRequestId) {
      fetchStatus(defaultRequestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRequestId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStatus(requestId);
  };

  const handleCopy = async () => {
    if (!requestId) return;
    try {
      await navigator.clipboard.writeText(requestId);
      toast.success(isSpanish ? 'ID copiado' : 'Request ID copied');
    } catch (_) {
      /* no-op */
    }
  };

  return (
    <Card
      className="border-blue-500/30 bg-blue-500/5"
      data-testid="deletion-status-tracker"
    >
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Search className="h-4 w-4 text-blue-600" />
            {isSpanish
              ? 'Verifica el estado de una solicitud'
              : 'Check status of an existing request'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isSpanish
              ? 'Pega tu ID (lo enviamos por correo cuando recibimos tu solicitud).'
              : 'Paste your request ID (we email it to you when we receive the request).'}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-2"
        >
          <Input
            type="text"
            placeholder="DEL-XXXXXXXX"
            value={requestId}
            onChange={(e) => setRequestId(e.target.value.toUpperCase())}
            className="font-mono uppercase"
            data-testid="deletion-status-input"
            maxLength={20}
            aria-label="Deletion request ID"
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid="deletion-status-submit-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Search className="h-4 w-4 mr-1.5" />
                {isSpanish ? 'Buscar' : 'Look up'}
              </>
            )}
          </Button>
          {requestId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              data-testid="deletion-status-copy-btn"
            >
              {isSpanish ? 'Copiar' : 'Copy'}
            </Button>
          )}
        </form>

        {error && (
          <Alert
            variant="destructive"
            data-testid="deletion-status-error"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {record && (
          <div className="mt-2 pt-2 border-t border-blue-500/20">
            <DeletionStatusTimeline record={record} isSpanish={isSpanish} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
